import { BPS } from "./constants";
import type { Market, SwapQuote } from "./types";

export function totalFeeBps(marketCapSol: number): number {
  if (marketCapSol >= 100_000) return 50;
  if (marketCapSol >= 80_000) return 75;
  if (marketCapSol >= 60_000) return 100;
  if (marketCapSol >= 40_000) return 150;
  if (marketCapSol >= 20_000) return 200;
  if (marketCapSol >= 10_000) return 250;
  return 300;
}

function percentLoss(actual: bigint, ideal: bigint): number {
  if (ideal <= 0n || actual >= ideal) return 0;
  return Number(((ideal - actual) * 1_000_000n) / ideal) / 10_000;
}

export function quoteBuy(
  market: Pick<Market, "virtualSolLamports" | "tokenReserveRaw" | "feeBps">,
  inputLamports: bigint,
  slippageBps: bigint,
): SwapQuote {
  if (inputLamports <= 0n || market.tokenReserveRaw <= 0n) {
    return { expectedOut: 0n, minOut: 0n, feeAmount: 0n, priceImpactPct: 0, totalImpactPct: 0 };
  }
  const feeAmount = (inputLamports * BigInt(market.feeBps)) / BPS;
  const feeAdjustedInput = inputLamports - feeAmount;
  const invariant = market.virtualSolLamports * market.tokenReserveRaw;
  const newTokenReserve = invariant / (market.virtualSolLamports + feeAdjustedInput);
  const expectedOut = market.tokenReserveRaw - newTokenReserve;
  const minOut = (expectedOut * (BPS - slippageBps)) / BPS;
  const idealAfterFee = (feeAdjustedInput * market.tokenReserveRaw) / market.virtualSolLamports;
  const idealBeforeFee = (inputLamports * market.tokenReserveRaw) / market.virtualSolLamports;
  return {
    expectedOut,
    minOut,
    feeAmount,
    priceImpactPct: percentLoss(expectedOut, idealAfterFee),
    totalImpactPct: percentLoss(expectedOut, idealBeforeFee),
  };
}

export function quoteSell(
  market: Pick<Market, "virtualSolLamports" | "tokenReserveRaw" | "feeBps">,
  tokenInputRaw: bigint,
  slippageBps: bigint,
): SwapQuote {
  if (tokenInputRaw <= 0n || market.tokenReserveRaw <= 0n) {
    return { expectedOut: 0n, minOut: 0n, feeAmount: 0n, priceImpactPct: 0, totalImpactPct: 0 };
  }
  const invariant = market.virtualSolLamports * market.tokenReserveRaw;
  const newVirtualSol = invariant / (market.tokenReserveRaw + tokenInputRaw);
  const grossOut = market.virtualSolLamports - newVirtualSol;
  const feeAmount = (grossOut * BigInt(market.feeBps)) / BPS;
  const expectedOut = grossOut - feeAmount;
  const minOut = (expectedOut * (BPS - slippageBps)) / BPS;
  const idealGross = (tokenInputRaw * market.virtualSolLamports) / market.tokenReserveRaw;
  const idealAfterFee = (idealGross * (BPS - BigInt(market.feeBps))) / BPS;
  return {
    expectedOut,
    minOut,
    feeAmount,
    priceImpactPct: percentLoss(expectedOut, idealAfterFee),
    totalImpactPct: percentLoss(expectedOut, idealGross),
  };
}
